/**
 * Utilitaires de validation de formulaires
 */

/**
 * Valide un montant
 * @param {number|string} amount - Montant à valider
 * @returns {{valid: boolean, error: string|null}}
 */
export const validateAmount = (amount) => {
  const numAmount = parseFloat(amount);
  
  if (isNaN(numAmount)) {
    return { valid: false, error: 'Le montant doit être un nombre' };
  }
  
  if (numAmount < 0) {
    return { valid: false, error: 'Le montant ne peut pas être négatif' };
  }
  
  if (numAmount === 0) {
    return { valid: false, error: 'Le montant doit être supérieur à 0' };
  }
  
  if (numAmount > 1000000) {
    return { valid: false, error: 'Le montant ne peut pas dépasser 1 000 000 €' };
  }
  
  // Vérifier max 2 décimales
  if (!/^\d+(\.\d{1,2})?$/.test(amount.toString())) {
    return { valid: false, error: 'Le montant ne peut avoir que 2 décimales maximum' };
  }
  
  return { valid: true, error: null };
};

/**
 * Valide une date
 * @param {string} date - Date à valider (format ISO)
 * @returns {{valid: boolean, error: string|null}}
 */
export const validateDate = (date) => {
  if (!date) {
    return { valid: false, error: 'La date est obligatoire' };
  }
  
  const selectedDate = new Date(date);
  const today = new Date();
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(today.getFullYear() - 5);
  const oneYearFuture = new Date();
  oneYearFuture.setFullYear(today.getFullYear() + 1);
  
  if (isNaN(selectedDate.getTime())) {
    return { valid: false, error: 'Date invalide' };
  }
  
  if (selectedDate < oneYearAgo) {
    return { valid: false, error: 'La date ne peut pas être antérieure à 5 ans' };
  }
  
  if (selectedDate > oneYearFuture) {
    return { valid: false, error: 'La date ne peut pas être dans plus d\'un an' };
  }
  
  return { valid: true, error: null };
};

/**
 * Valide un libellé
 * @param {string} libelle - Libellé à valider
 * @returns {{valid: boolean, error: string|null}}
 */
export const validateLibelle = (libelle) => {
  if (!libelle || !libelle.trim()) {
    return { valid: false, error: 'Le libellé est obligatoire' };
  }
  
  if (libelle.trim().length < 3) {
    return { valid: false, error: 'Le libellé doit contenir au moins 3 caractères' };
  }
  
  if (libelle.length > 200) {
    return { valid: false, error: 'Le libellé ne peut pas dépasser 200 caractères' };
  }
  
  return { valid: true, error: null };
};

/**
 * Valide un formulaire de transaction complet
 * @param {Object} formData - Données du formulaire
 * @returns {{valid: boolean, errors: Object}}
 */
export const validateTransactionForm = (formData) => {
  const errors = {};
  
  // Valider libellé
  const libelleValidation = validateLibelle(formData.libelle);
  if (!libelleValidation.valid) {
    errors.libelle = libelleValidation.error;
  }
  
  // Valider montant
  const amountValidation = validateAmount(formData.amount);
  if (!amountValidation.valid) {
    errors.amount = amountValidation.error;
  }
  
  // Valider date
  const dateValidation = validateDate(formData.date);
  if (!dateValidation.valid) {
    errors.date = dateValidation.error;
  }
  
  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Affiche une confirmation pour les montants importants
 * @param {number} amount - Montant
 * @param {string} type - Type de transaction ('R' ou 'D')
 * @returns {boolean} - True si confirmé
 */
export const confirmLargeAmount = (amount, type) => {
  const threshold = type === 'R' ? 1000 : 500; // Seuil différent pour revenus et dépenses
  
  if (amount >= threshold) {
    return window.confirm(
      `⚠️ Montant important détecté : ${amount.toFixed(2)} €\n\n` +
      `Êtes-vous sûr de vouloir ${type === 'R' ? 'enregistrer ce revenu' : 'enregistrer cette dépense'} ?`
    );
  }
  
  return true;
};
